<template>
  <div class="panel" data-test="timeline-range-slider">
    <div class="panel-top">
      <div class="header">
        <div class="header-label">
          <ys-text type="metadata1-bold" class="tick-day-of-week">
            {{ tickDayOfWeek }}
          </ys-text>
          <ys-text type="metadata1-bold" class="tick-separator">•</ys-text>
          <ys-text type="metadata1-bold" class="tick-date">
            {{ tickDate }} • {{ tickTime }}
          </ys-text>
          <ys-text v-if="isNow" type="metadata1-bold">(Now)</ys-text>
        </div>
        <ys-button
          class="icon-btn"
          size="small"
          variant="ghost"
          :disabled="isNow"
          @click="setToNow"
        >
          <ys-icon slot="icon" icon="ysRefresh" />
          <ys-text type="metadata1">Now</ys-text>
        </ys-button>
      </div>

      <div class="track-wrap">
        <input
          class="range"
          type="range"
          :min="0"
          :max="tickCount - 1"
          step="1"
          :value="thumbIndex"
          aria-label="Timeline tick slider"
          @input="onInput"
        />

        <div
          class="pill"
          :style="{ left: `calc(${percentLeft}% )` }"
          aria-hidden="true"
        />

        <div class="ticks" aria-hidden="true">
          <div
            v-for="tick in visibleTicks"
            :key="tick.index"
            class="tick"
            :class="{ now: tick.isNow }"
            :style="{ left: `${tick.position}%` }"
          />
        </div>
      </div>
      <div class="labels" aria-hidden="true">
        <ys-text type="metadata1" class="label label-start">
          -{{ props.daysPrior }}d
        </ys-text>
        <ys-text
          type="metadata1"
          class="label label-now"
          :style="{ left: `${nowPosition}%` }"
        >
          now
        </ys-text>
        <ys-text type="metadata1" class="label label-end">
          +{{ props.daysAfter }}d
        </ys-text>
      </div>
    </div>
    <div v-if="$slots.legend" class="panel-bottom">
      <slot name="legend" />
    </div>
  </div>
</template>
<script setup lang="ts">
///// imports /////
import { computed, ref, onMounted } from 'vue'
import clamp from 'lodash/clamp'
import '@onxmaps/yellowstone-ui/ys-button'
import '@onxmaps/yellowstone-ui/ys-text'
import '@onxmaps/yellowstone-ui/ys-icon'
import '@onxmaps/yellowstone-ui/icons/ysRefresh'
import { MS_DAY, MS_HOUR } from '@/fish/components/ui/wind/utils'

import marketingEventService from '@/services/marketing-event-service'

///// types /////
interface Props {
  daysPrior?: number
  daysAfter?: number
}

///// props/emits /////
const props = withDefaults(defineProps<Props>(), {
  daysPrior: 3,
  daysAfter: 5,
})

const emit = defineEmits<{
  (e: 'range-change', payload: { tickIndex: number }): void
}>()

///// refs and variables /////
const currentDate = new Date()
const thumbIndex = ref<number>(0)

///// computed /////
const nowIdx = computed(() => {
  return Math.round(
    (currentDate.getTime() - rangeStart.value.getTime()) / MS_HOUR
  )
})

const isNow = computed(() => thumbIndex.value === nowIdx.value)

// Range offsets based on props (hours from now)
const rangeStartOffset = computed(() => -props.daysPrior * MS_DAY)
const rangeEndOffset = computed(() => props.daysAfter * MS_DAY)

const selectedDate = computed(
  () => new Date(rangeStart.value.getTime() + thumbIndex.value * MS_HOUR)
)

const tickDayOfWeek = computed(() =>
  selectedDate.value.toLocaleString(undefined, { weekday: 'short' })
)

const tickDate = computed(() => {
  const month = selectedDate.value.toLocaleString(undefined, { month: 'short' })
  const day = selectedDate.value.toLocaleString(undefined, { day: 'numeric' })
  return `${month} ${day}`
})

const tickTime = computed(() =>
  selectedDate.value
    .toLocaleString(undefined, { hour: 'numeric' })
    .replace(':00', '')
)

const rangeStart = computed(
  () => new Date(currentDate.getTime() + rangeStartOffset.value)
)

const rangeEnd = computed(
  () => new Date(currentDate.getTime() + rangeEndOffset.value)
)

const rangeMs = computed(
  () => rangeEnd.value.getTime() - rangeStart.value.getTime()
)

const tickCount = computed(() => Math.floor(rangeMs.value / MS_HOUR) + 1)

const percentLeft = computed(() => {
  if (tickCount.value <= 1) {
    return 0
  }
  return (thumbIndex.value / (tickCount.value - 1)) * 100
})

const nowPosition = computed(() => {
  if (tickCount.value <= 1) {
    return 0
  }
  return (nowIdx.value / (tickCount.value - 1)) * 100
})

const visibleTicks = computed(() => {
  const ticks = []

  for (let i = 0; i < tickCount.value; i += 24) {
    ticks.push({
      index: i,
      isNow: i === nowIdx.value,
      position: (i / (tickCount.value - 1)) * 100,
    })
  }

  return ticks
})

///// functions /////
const emitRangeChange = () => {
  const idx = clamp(thumbIndex.value, 0, tickCount.value - 1)
  marketingEventService.publish('wind_snapshots_viewed', {
    index: thumbIndex.value - nowIdx.value,
  })
  emit('range-change', {
    tickIndex: idx,
  })
}

const onInput = (e: Event) => {
  const v = Number((e.target as HTMLInputElement).value)
  thumbIndex.value = clamp(Math.round(v), 0, tickCount.value - 1)
  emitRangeChange()
}

const setToNow = () => {
  thumbIndex.value = clamp(nowIdx.value, 0, tickCount.value - 1)
  emitRangeChange()
}

///// lifecycle /////
onMounted(() => {
  // Always initialize to current time on mount
  setToNow()
})
</script>
<style lang="scss" scoped>
@use '@/styles/system/_breakpoints.scss';

.panel {
  border-radius: var(--ys-radius-150);
  width: 100%;
  overflow: hidden;

  @include breakpoints.mobile {
    width: 80%;
  }
}

.panel-top {
  background: var(--ys-color-surface-primary);
  padding-bottom: var(--ys-spacing-100);
  padding-top: var(--ys-spacing-50);
}

.panel-bottom {
  background-color: var(--ys-color-surface-secondary);
  padding: var(--ys-spacing-100) var(--ys-spacing-200);
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: var(--ys-spacing-200);
}

.header-label {
  display: flex;
  align-items: center;
  gap: var(--ys-spacing-50);
}

.tick-day-of-week {
  width: 20px;
}

.tick-time {
  width: 35px;
}

.tick-separator {
  text-align: center;
}

.icon-btn {
  margin-right: var(--ys-spacing-50);
}

.track-wrap {
  position: relative;
  height: 42px;
  display: flex;
  align-items: flex-start;
  padding-top: var(--ys-spacing-50);
  margin: 0 var(--ys-spacing-300);
}

.ticks {
  position: absolute;
  left: 0;
  right: 0;
  top: 28px;
  height: 14px;
  pointer-events: none;
  margin-top: var(--ys-spacing-50);
}

.tick {
  position: absolute;
  width: 4px;
  height: 4px;
  top: 0;
  border-radius: 50%;
  background: var(--ys-color-surface-tertiary);
}

.tick.now,
.tick:first-child,
.tick:last-child {
  height: 6px;
  border-radius: 30%;
  background: var(--ys-color-surface-tertiary);
}

.labels {
  position: relative;
  margin: 0 var(--ys-spacing-300);
  display: flex;
  justify-content: space-between;
}

.label {
  color: var(--ys-color-text-secondary);
}

.label-now {
  position: absolute;
  transform: translateX(-50%);
  text-align: center;
}

.range {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  margin: 0;
  height: 28px;
  background: transparent;
  -webkit-appearance: none;
  appearance: none;
  z-index: 2;
}

.range::-webkit-slider-runnable-track {
  height: 4px;
  background: var(--ys-color-surface-secondary);
  border-radius: 2px;
}

.range::-moz-range-track {
  height: 2px;
  background: var(--ys-color-surface-secondary);
  border-radius: 2px;
}

.range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 1px;
  height: 1px;
  background: transparent;
  border: 0;
}

.range::-moz-range-thumb {
  width: 1px;
  height: 1px;
  background: transparent;
  border: 0;
}

.pill {
  position: absolute;
  top: 14px;
  transform: translate(-50%, -50%);
  width: 39px;
  height: 26px;
  border-radius: 13px;
  background: var(--ys-color-surface-constant-primary);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.35);
  z-index: 3;
  pointer-events: none;
}
</style>
